import { Container, Graphics, Sprite, Text } from "pixi.js";
import type { RefObject } from "react";
import { vibrateForEffect, type EffectChannel, type EffectSettings } from "../effects";
import type { Cascade } from "../types";
import {
  BOARD_PIXELS,
  BOARD_SIZE,
  cascadeEffectStrength,
  COLORS,
  DISSOLVE_MS,
  FLASH_MS,
  TILE_SIZE,
} from "./constants";
export function createMatchEffects({
  currentEffectScale,
  reducedMotion,
  animationActive,
  motionLayer,
  effectLayer,
  host,
  nextFrame,
  effectSettingsRef,
  playBoardRecoil,
  tileSprite,
}: {
  currentEffectScale: (channel?: EffectChannel) => number;
  reducedMotion: boolean;
  animationActive: (generation: number) => boolean;
  motionLayer: Container;
  effectLayer: Container;
  host: HTMLDivElement;
  nextFrame: (callback: FrameRequestCallback) => number;
  effectSettingsRef: RefObject<EffectSettings>;
  playBoardRecoil: (cascade: Cascade) => void;
  tileSprite: (tile: number, tier: 1 | 2) => Sprite | null;
}) {
  const playLandingImpact = (
    matched: number[],
    next: number[],
    landingRows: Map<number, number>,
    currentGeneration: number,
  ) => {
    const effectScale = currentEffectScale("particles");
    if (reducedMotion || effectScale <= 0 || !animationActive(currentGeneration)) return;
    const columns = new Map<number, number>();
    for (const index of matched) {
      const col = index % BOARD_SIZE;
      columns.set(col, (columns.get(col) ?? 0) + 1);
    }
    if (landingRows.size === 0) return;
    const impact = new Container();
    const ripples: Graphics[] = [];
    const dust: Array<{ view: Graphics; x: number; y: number; vx: number; vy: number }> = [];
    for (const [col, landingRow] of landingRows) {
      const removed = columns.get(col) ?? 1;
      const x = col * TILE_SIZE + TILE_SIZE / 2;
      const landingY = (landingRow + 1) * TILE_SIZE - 3;
      const color = COLORS[next[landingRow * BOARD_SIZE + col] ?? 0] ?? "#d5c27b";
      const ripple = new Graphics();
      ripple
        .rect(-6, -1, 4, 2)
        .rect(2, -1, 4, 2)
        .fill({ color: "#d5ba83", alpha: 0.35 + effectScale * 0.65 });
      ripple.x = x;
      ripple.y = landingY;
      ripple.scale.set(0.45);
      ripples.push(ripple);
      impact.addChild(ripple);
      const pieces = Math.min(7, Math.max(1, Math.round((2 + removed) * effectScale * 1.25)));
      for (let piece = 0; piece < pieces; piece++) {
        const fragment = new Graphics();
        fragment.rect(0, 0, piece % 3 === 0 && effectScale > 0.65 ? 4 : 3, 2).fill({ color });
        const spread = (piece - (pieces - 1) / 2) * 8;
        const particle = {
          view: fragment,
          x: x + spread * 0.3,
          y: landingY - 2,
          vx: spread,
          vy: -(18 + piece * 4),
        };
        fragment.x = particle.x;
        fragment.y = particle.y;
        dust.push(particle);
        impact.addChild(fragment);
      }
    }
    motionLayer.addChild(impact);
    host.dataset.fallImpactCount = String(Number(host.dataset.fallImpactCount ?? "0") + 1);
    const started = performance.now();
    const duration = 120;
    const finish = () => {
      if (impact.destroyed) return;
      if (impact.parent === motionLayer) motionLayer.removeChild(impact);
      impact.destroy({ children: true });
    };
    const step = (now: number) => {
      if (!animationActive(currentGeneration)) {
        finish();
        return;
      }
      const progress = Math.min(1, (now - started) / duration);
      for (const ripple of ripples) {
        ripple.scale.set(0.45 + progress * 1.35, 0.45 + progress * 0.55);
        ripple.alpha = 1 - progress;
      }
      for (const particle of dust) {
        const elapsed = (now - started) / 1000;
        particle.view.x = particle.x + particle.vx * elapsed;
        particle.view.y = particle.y + particle.vy * elapsed + 34 * elapsed * elapsed;
        particle.view.alpha = (1 - progress) * effectScale;
        particle.view.scale.set(1 - progress * 0.45);
      }
      if (progress < 1) nextFrame(step);
      else finish();
    };
    nextFrame(step);
  };
  const flashMatches = (cascade: Cascade, sourceBoard: number[], currentGeneration: number) => {
    const matched = cascade.matched ?? [];
    const matchSize = Math.max(0, ...(cascade.groups ?? []).map((group) => group.count));
    const cascadeStrength = cascadeEffectStrength(cascade);
    const powerMatch = matchSize >= 4;
    const impactScale = currentEffectScale("impact");
    const particleScale = currentEffectScale("particles");
    const lightingScale = currentEffectScale("lighting");
    const effectScale = Math.max(impactScale, particleScale, lightingScale);
    effectLayer.removeChildren().forEach((child) => child.destroy({ children: true }));
    if (effectScale <= 0) return;
    host.dataset.effectIntensity = String(
      Math.round(
        Math.max(
          ...Object.values(effectSettingsRef.current).map((setting) =>
            setting.enabled ? setting.intensity : 0,
          ),
        ),
      ),
    );
    playBoardRecoil(cascade);
    const flashes: Graphics[] = [];
    const poweredTiles: Sprite[] = [];
    const particles: Array<{ view: Graphics; x: number; y: number; vx: number; vy: number }> = [];
    const bursts: Array<{
      view: Graphics;
      x: number;
      y: number;
      strength: number;
      strike: boolean;
    }> = [];
    let powerFrame: Graphics | null = null;
    let feedbackLabel: Text | null = null;
    if (powerMatch && lightingScale > 0) {
      host.dataset.lastMatchSize = String(matchSize);
      host.dataset.powerMatchCount = String(Number(host.dataset.powerMatchCount ?? "0") + 1);
      powerFrame = new Graphics();
      powerFrame.roundRect(5, 5, BOARD_PIXELS - 10, BOARD_PIXELS - 10, 5).stroke({
        color: matchSize >= 5 ? "#f0d49a" : "#caa45f",
        width: 2,
        alpha: 0.3 + effectScale * 0.7,
      });
      effectLayer.addChild(powerFrame);
      if (!reducedMotion && impactScale >= 0.35) {
        vibrateForEffect(effectSettingsRef.current, matchSize >= 5 ? 1.25 : 0.8);
      }
    }
    if (!reducedMotion && (impactScale > 0 || lightingScale > 0)) {
      for (const group of cascade.groups ?? []) {
        if (group.cells.length === 0) continue;
        const center = group.cells.reduce(
          (point, cell) => ({
            x: point.x + (cell % BOARD_SIZE) * TILE_SIZE + TILE_SIZE / 2,
            y: point.y + Math.floor(cell / BOARD_SIZE) * TILE_SIZE + TILE_SIZE / 2,
          }),
          { x: 0, y: 0 },
        );
        const x = center.x / group.cells.length;
        const y = center.y / group.cells.length;
        const strength =
          (0.65 + cascadeStrength.boost * 0.85 + Math.max(0, group.count - 3) * 0.035) *
          Math.max(impactScale, lightingScale);
        const radius = 8 + group.count * 1.05 + cascadeStrength.boost * 5;
        const burst = new Graphics();
        for (let ray = 0; ray < 6; ray++) {
          const angle = (ray * Math.PI) / 3 + 0.3;
          for (let step = 1; step <= 3; step++) {
            const size = 5 - step;
            burst.rect(
              Math.round((Math.cos(angle) * radius * step) / 3),
              Math.round((Math.sin(angle) * radius * step) / 3),
              size,
              size,
            );
          }
        }
        burst.fill({ color: COLORS[group.tile] ?? "#f1d79d", alpha: 0.42 + effectScale * 0.46 });
        burst.rect(-3, -3, 6, 6).fill({ color: "#fff4dc", alpha: 0.24 + effectScale * 0.38 });
        burst.x = x;
        burst.y = y;
        burst.scale.set(0.45);
        effectLayer.addChild(burst);
        bursts.push({ view: burst, x, y, strength, strike: group.tile === 0 });
      }
    }
    let particleBudget = Math.round(260 * particleScale);
    for (const index of matched) {
      const col = index % BOARD_SIZE;
      const row = Math.floor(index / BOARD_SIZE);
      const tile = sourceBoard[index] ?? 0;
      if (powerMatch) {
        const powered = tileSprite(tile, 2);
        if (powered) {
          powered.anchor.set(0.5);
          powered.x = col * TILE_SIZE + TILE_SIZE / 2;
          powered.y = row * TILE_SIZE + TILE_SIZE / 2;
          poweredTiles.push(powered);
          effectLayer.addChild(powered);
        }
      }
      const flash = new Graphics();
      flash
        .rect(col * TILE_SIZE + 2, row * TILE_SIZE + 2, TILE_SIZE - 4, TILE_SIZE - 4)
        .fill({
          color: "#ffffff",
          alpha:
            (powerMatch ? 0.92 : 0.7) *
            (0.28 + lightingScale * 0.72) *
            (0.82 + cascadeStrength.boost * 0.18),
        })
        .stroke({ color: powerMatch ? "#f2dba8" : COLORS[tile], width: powerMatch ? 2 : 1 });
      flashes.push(flash);
      effectLayer.addChild(flash);
      if (!reducedMotion && particleScale > 0) {
        const baseParticles = powerMatch ? Math.min(12, matchSize + 5) : 7;
        const particleCount = Math.max(
          0,
          Math.min(
            particleBudget,
            Math.max(
              1,
              Math.round(
                baseParticles * (0.45 + particleScale * 0.75) * (1 + cascadeStrength.boost * 0.7),
              ),
            ),
          ),
        );
        particleBudget -= particleCount;
        for (let piece = 0; piece < particleCount; piece++) {
          const fragment = new Graphics();
          fragment
            .rect(0, 0, powerMatch && piece % 3 === 0 ? 5 : 4, powerMatch ? 3 : 4)
            .fill({ color: powerMatch && piece % 2 === 0 ? "#efd398" : COLORS[tile] });
          const angle = (piece / particleCount) * Math.PI * 2 + (index % 3) * 0.25;
          const velocityScale =
            (0.75 + particleScale * 0.45) * (0.88 + cascadeStrength.boost * 0.22);
          const particle = {
            view: fragment,
            x: col * TILE_SIZE + TILE_SIZE / 2,
            y: row * TILE_SIZE + TILE_SIZE / 2,
            vx: Math.cos(angle) * ((powerMatch ? 34 : 22) + (index % 4) * 3) * velocityScale,
            vy: Math.sin(angle) * ((powerMatch ? 30 : 20) + (index % 4) * 3) * velocityScale - 9,
          };
          fragment.x = particle.x;
          fragment.y = particle.y;
          particles.push(particle);
          effectLayer.addChild(fragment);
        }
      }
    }
    if (powerMatch || cascade.layer > 1) {
      const matchLabel =
        matchSize >= 7 ? "七连" : matchSize === 6 ? "六连" : matchSize === 5 ? "五连" : "四连";
      feedbackLabel = new Text({
        text: `${powerMatch ? matchLabel : ""}${powerMatch && cascade.layer > 1 ? " · " : ""}${cascade.layer > 1 ? `连锁 x${cascade.layer}` : ""}`,
        style: {
          fontFamily: "sans-serif",
          fontSize: powerMatch ? 20 : 15,
          fontWeight: "900",
          fill: powerMatch ? "#f1d79d" : "#cfad73",
          stroke: { color: "#15111a", width: 5 },
        },
      });
      feedbackLabel.anchor.set(0.5);
      feedbackLabel.x = BOARD_PIXELS / 2;
      feedbackLabel.y = BOARD_PIXELS / 2;
      feedbackLabel.scale.set(powerMatch ? 0.72 : 1);
      effectLayer.addChild(feedbackLabel);
    }
    const started = performance.now();
    const flashDuration = reducedMotion ? 30 : FLASH_MS;
    const dissolveDuration = reducedMotion ? 40 : DISSOLVE_MS;
    const duration = flashDuration + dissolveDuration;
    const step = (now: number) => {
      if (!animationActive(currentGeneration)) return;
      const progress = Math.min(1, (now - started) / duration);
      const flashProgress = Math.min(1, (now - started) / flashDuration);
      const dissolveProgress = Math.min(
        1,
        Math.max(0, (now - started - flashDuration) / dissolveDuration),
      );
      for (const flash of flashes) flash.alpha = 1 - flashProgress;
      for (const powered of poweredTiles) {
        powered.alpha = (1 - dissolveProgress) * (0.45 + effectScale * 0.55);
        powered.scale.set(1 + dissolveProgress * 0.08);
      }
      if (powerFrame) {
        powerFrame.alpha = 1 - progress;
        powerFrame.scale.set(1 + progress * 0.025);
        powerFrame.x = -BOARD_PIXELS * progress * 0.0125;
        powerFrame.y = -BOARD_PIXELS * progress * 0.0125;
      }
      if (feedbackLabel && powerMatch) {
        feedbackLabel.alpha =
          (1 - Math.max(0, dissolveProgress - 0.45) / 0.55) * (0.45 + effectScale * 0.55);
        feedbackLabel.scale.set(0.88 + Math.min(1, progress * 2.4) * 0.12);
      }
      for (const burst of bursts) {
        const burstProgress = Math.min(1, progress * 1.25);
        const scale = 0.45 + burstProgress * (0.7 + burst.strength * 0.55);
        burst.view.scale.set(scale);
        burst.view.rotation = (burst.strike ? -0.16 : 0.08) * burstProgress;
        burst.view.alpha = (1 - burstProgress) * (0.4 + effectScale * 0.6);
        burst.view.x = burst.x;
        burst.view.y = burst.y;
      }
      for (const particle of particles) {
        particle.view.x = Math.round(particle.x + particle.vx * dissolveProgress);
        particle.view.y = Math.round(
          particle.y + particle.vy * dissolveProgress + 24 * dissolveProgress * dissolveProgress,
        );
        particle.view.alpha =
          flashProgress < 1 ? 0 : (1 - dissolveProgress) * (0.35 + effectScale * 0.65);
        particle.view.scale.set(1 - dissolveProgress * 0.55);
      }
      if (progress < 1) nextFrame(step);
    };
    nextFrame(step);
  };
  return { playLandingImpact, flashMatches };
}
